import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import {
  useAddPatientStaffAssignment,
  usePatientStaffAssignments,
  useRemovePatientStaffAssignment,
} from '@/hooks/usePatientStaffAssignments';
import { memberRoleIsPractitioner } from '@/lib/practitionerRole';
import { Avatar } from '@/components/ui/Avatar';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { StatusChip } from '@/components/ui/Chip';
import { PatientTabEmpty, PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

interface PatientStaffAssignmentsTabProps {
  clinicId: string;
  patientId: string;
  /** Users who may edit the patient can add/remove links; others see the list read-only. */
  canEdit: boolean;
}

/**
 * Patient page "Tilknytninger" tab: the clinic staff (behandlere and other employees) linked
 * to the patient. Several can be linked; the patient list's "Kun mine patienter" uses these.
 */
export function PatientStaffAssignmentsTab({ clinicId, patientId, canEdit }: PatientStaffAssignmentsTabProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const { data: assignments, isLoading, error, refetch } = usePatientStaffAssignments(patientId);
  const { data: members, isLoading: membersLoading } = useClinicMembers(canEdit ? clinicId : null);
  const { data: roles } = useClinicRoles(clinicId);
  const addAssignment = useAddPatientStaffAssignment();
  const removeAssignment = useRemovePatientStaffAssignment();
  const pending = addAssignment.isPending || removeAssignment.isPending;
  const assignedIds = useMemo(() => new Set((assignments ?? []).map((a) => a.user_id)), [assignments]);

  const practitionerIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of members ?? []) if (memberRoleIsPractitioner(m, roles ?? [])) ids.add(m.user_id);
    return ids;
  }, [members, roles]);

  const options = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return [...(members ?? [])]
      .filter((m) => !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      .sort((a, b) => {
        const pa = practitionerIds.has(a.user_id) ? 0 : 1;
        const pb = practitionerIds.has(b.user_id) ? 0 : 1;
        return pa - pb || a.name.localeCompare(b.name, 'da');
      });
  }, [members, filter, practitionerIds]);

  async function toggle(userId: string, assign: boolean) {
    try {
      if (assign) await addAssignment.mutateAsync({ patientId, userId });
      else await removeAssignment.mutateAsync({ patientId, userId });
    } catch (err) {
      console.error('patient staff assignment failed', err);
      toast.error(t('patients.assignments.save_error'));
    }
  }

  const noneAssigned = !isLoading && !error && (assignments ?? []).length === 0;

  return (
    <PatientTabFrame
      title={t('patients.tabs.tilknytninger')}
      count={(assignments ?? []).length}
      loading={isLoading}
      error={error ? t('patients.assignments.load_error') : undefined}
      onRetry={() => void refetch()}
    >
      {!canEdit ? (
        <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>{t('patients.assignments.read_only')}</p>
      ) : null}

      {canEdit ? (
        <div className="mt-s5">
          <div className="flex flex-col gap-s3 nav:flex-row nav:items-end nav:justify-between">
            <div>
              <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('patients.assignments.select_title')}</h3>
              <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{t('patients.assignments.select_subtitle')}</p>
            </div>
            <Input
              id="assignment-search"
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('patients.assignments.search_placeholder')}
              aria-label={t('patients.assignments.search_placeholder')}
              leadingIcon={<Search className="h-4 w-4" />}
              className="w-full nav:w-64"
            />
          </div>

          {membersLoading ? (
            <div className="mt-s4 space-y-s2">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : options.length === 0 ? (
            <PatientTabEmpty
              icon={<Users className="h-7 w-7" />}
              title={t('patients.assignments.no_staff')}
            />
          ) : (
            <ul className="mt-s4 divide-y divide-ds-line" aria-label={t('patients.assignments.select_title')}>
              {options.map((m) => {
                const checked = assignedIds.has(m.user_id);
                return (
                  <li key={m.user_id} className="flex items-center gap-s3 py-s3">
                    <Checkbox
                      checked={checked}
                      disabled={pending}
                      onChange={(e) => void toggle(m.user_id, e.target.checked)}
                      aria-label={m.name}
                    />
                    <Avatar name={m.name} size={40} />
                    <span className="min-w-0 flex-1">
                      <span className={cn(TYPE.body, 'block truncate text-ds-text')}>{m.name}</span>
                      {m.email && m.email !== m.name ? (
                        <span className={cn(TYPE.small, 'block truncate text-ds-muted')}>{m.email}</span>
                      ) : null}
                    </span>
                    {practitionerIds.has(m.user_id) ? (
                      <StatusChip tone="completed">{t('patients.assignments.practitioner_badge')}</StatusChip>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : noneAssigned ? (
        <PatientTabEmpty
          icon={<Users className="h-7 w-7" />}
          title={t('patients.assignments.none')}
          description={t('patients.assignments.empty_hint')}
        />
      ) : (
        <ul className="mt-s4 divide-y divide-ds-line" aria-label={t('patients.assignments.title')}>
          {(assignments ?? []).map((a) => (
            <li key={a.id} className="flex items-center gap-s3 py-s3">
              <Avatar name={a.name || t('patients.assignments.unknown_user')} size={40} />
              <span className={cn(TYPE.body, 'text-ds-text')}>
                {a.name || t('patients.assignments.unknown_user')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </PatientTabFrame>
  );
}
